import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useT } from "../../i18n";

interface ClientRow {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  social_links: string[];
  employee_bracket: string | null;
  hired_before: boolean | null;
  hired_what: string | null;
  ai_tools: string[];
  other_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface DemandRow {
  id: string;
  kind: "text" | "audio" | "file";
  text_content: string | null;
  transcript: string | null;
  file_names: string[];
  status: string;
  created_at: string;
}

interface BookingRow {
  id: string;
  start_at: string;
  end_at: string;
  meet_url: string | null;
  html_link: string | null;
  status: string;
  created_at: string;
}

interface ClientDetailResponse {
  client: ClientRow;
  demands: DemandRow[];
  bookings: BookingRow[];
}

export function ClientDetail() {
  const { t, lang } = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ClientDetailResponse | null>(null);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/admin/clients/${id}`)
      .then((r) => {
        if (r.status === 401) {
          navigate("/admin");
          return null;
        }
        return r.json() as Promise<ClientDetailResponse>;
      })
      .then((d) => {
        if (d) setData(d);
      })
      .catch(() => {});
  }, [id, navigate]);

  if (!data || !data.client) {
    return (
      <div className="panel center muted">
        <Link to="/admin/leads">{t("clientDetailBack")}</Link>
      </div>
    );
  }

  const { client, demands, bookings } = data;
  const demand = demands[0];

  return (
    <div className="panel wide">
      <Link to="/admin/leads">← {t("clientDetailBack")}</Link>
      <h2 style={{ marginTop: 10 }}>{client.name}</h2>

      <div className="section-label">{t("clientAnswersTitle")}</div>
      <div className="grid2">
        <div>
          <strong className="meta-k">{t("email")}</strong>
          <div>{client.email}</div>
        </div>
        <div>
          <strong className="meta-k">{t("phone")}</strong>
          <div>{client.phone}</div>
        </div>
        <div>
          <strong className="meta-k">{t("intakeStepEmployees")}</strong>
          <div>{client.employee_bracket}</div>
        </div>
        <div>
          <strong className="meta-k">{t("intakeStepHired")}</strong>
          <div>{client.hired_before ? client.hired_what ?? t("yes") : t("no")}</div>
        </div>
      </div>
      <p style={{ marginTop: 10 }}>{client.ai_tools.join(", ")}</p>

      <div className="section-label">{t("clientDemandTitle")}</div>
      <p className="lead">{demand?.text_content ?? demand?.transcript ?? "—"}</p>
      {demand?.file_names && demand.file_names.length > 0 && (
        <ul className="filelist">
          {demand.file_names.map((f) => (
            <li key={f}>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="section-label">{t("clientBookingsTitle")}</div>
      {bookings.length === 0 ? (
        <p className="muted">{t("clientNoBookings")}</p>
      ) : (
        <ul className="events">
          {bookings.map((b) => (
            <li key={b.id}>
              {new Intl.DateTimeFormat(lang === "pt" ? "pt-BR" : "en-US", {
                dateStyle: "full",
                timeStyle: "short",
                timeZone: "America/Sao_Paulo",
              }).format(new Date(b.start_at))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
